<template>
  <div class="encounter-medication-dss">
    <v-form
      v-on:submit.prevent
      class="encounter-form"
    >
      <v-simple-table class="encounter-form__table">
        <template v-slot:default>
          <transition-group tag="tbody" name="slide">
          <template
              v-for="(questionConfig) in $store.getters['encounter/encounterConfigDisplayMedicationDSS']"
          >
            <tr v-if="questionConfig.key === 'STEMI_NSTEMI'" :key="questionConfig.key" :class="'QUESTION_' + questionConfig.key">
              <td class="encounter-medication-dss__question">
                {{ $ucfirst($t(`questionnaires.DSS_INFO.questions.${questionConfig.key}.question`, { catalog: 'questionnaires', language : 'en' }))|stripHTML }}
                <p class="text-language-note" v-if="!isDefaultLanguage">{{ $ucfirst($t(`questionnaires.DSS_INFO.questions.${questionConfig.key}.question`, { catalog: 'questionnaires' }))|stripHTML }}</p>
              </td>
              <td>
                <v-radio-group
                    v-model="self()[questionConfig.key]"
                    :errorMessages="errorMessages[questionConfig.key]"
                    @change="() => setErrorMessage(questionConfig.key)"
                    row
                    :disabled="!canEditVisitData"
                >
                  <v-radio
                      :label="$ucfirst($t('encounter.STEMI'))"
                      value="STEMI"
                  ></v-radio>
                  <v-radio
                      :label="$ucfirst($t('encounter.NSTEMI'))"
                      value="NSTEMI"
                  ></v-radio>
                </v-radio-group>
              </td>
            </tr>

            <tr v-else-if="questionConfig.key === 'PCI_DATE'" :key="questionConfig.key" :class="'QUESTION_' + questionConfig.key">
              <td class="encounter-medication-dss__question">
                {{ $ucfirst($t(`questionnaires.DSS_INFO.questions.${questionConfig.key}.question`, { catalog: 'questionnaires', language : 'en'  }))|stripHTML }}
                <p class="text-language-note" v-if="!isDefaultLanguage">{{ $ucfirst($t(`questionnaires.DSS_INFO.questions.${questionConfig.key}.question`, { catalog: 'questionnaires' }))|stripHTML }}</p>
              </td>
              <td>
                <form-date-picker
                    v-model="self()['PCI_DATE']"
                    :max="today"
                    :errorMessages="errorMessages['PCI_DATE']"
                    @change="() => setErrorMessage('PCI_DATE')"
                    :disabled="!canEditVisitData"
                ></form-date-picker>
              </td>
            </tr>

            <tr v-else :key="questionConfig.key" :class="'QUESTION_' + questionConfig.key">
              <td class="encounter-medication-dss__question">
                {{ $ucfirst($store.getters['encounter/getTranslationForDSSQuestion'](questionConfig.key, 'en'))|stripHTML }}
                <p class="text-language-note" v-if="!isDefaultLanguage">{{ $ucfirst($store.getters['encounter/getTranslationForDSSQuestion'](questionConfig.key))|stripHTML }}</p>
              </td>
              <td>
                <v-radio-group
                    v-model="self()[questionConfig.key]"
                    :errorMessages="errorMessages[questionConfig.key]"
                    @change="() => setErrorMessage(questionConfig.key)"
                    row
                    :disabled="!canEditVisitData"
                >
                  <v-radio
                      :label="$ucfirst($t('buttons.yes', {catalog: 'app_mobile'}))"
                      value="YES"
                  ></v-radio>
                  <v-radio
                      :label="$ucfirst($t('buttons.no', {catalog: 'app_mobile'}))"
                      value="NO"
                  ></v-radio>
                </v-radio-group>
              </td>
            </tr>
          </template>

          <tr v-if="$store.getters['encounter/versionInt'] === 6" key="LDL">
            <td class="encounter-medication-dss__question">
              <!-- Note: English by specs-->
              {{ 'LDL' }}
            </td>
            <td>
              <v-text-field
                v-model="LDL"
                :suffix="LDLUnitLabel"
                outlined
                dense
                :errorMessages="errorMessages.LDLInPreferredUnit"
                @blur="() => setErrorMessage('LDLInPreferredUnit')"
                :disabled="!canEditVisitData"
                type="number"
                min="0"
              ></v-text-field>
            </td>
          </tr>
          <tr v-if="$store.getters['encounter/versionInt'] === 6" key="eGFR">
            <td class="encounter-medication-dss__question">
              <!-- Note: English by specs-->
              {{ 'eGFR' }}
            </td>
            <td>
              <v-text-field
                v-model="eGFR"
                outlined
                dense
                :errorMessages="errorMessages.eGFR"
                @blur="() => setErrorMessage('eGFR')"
                :disabled="!canEditVisitData"
                type="number"
                min="0"
                suffix="ml/min/1.73m2"
              ></v-text-field>
            </td>
          </tr>
          </transition-group>
        </template>
      </v-simple-table>
    </v-form>
  </div>
</template>

<script>
import FormDatePicker from '../../components/Parts/FormDatePicker';
import questions from 'dashboard/store/encounter/questionnaires/medication-dss';
import encounterMixin from 'dashboard/mixins/encounter.mixin';
import moment from "moment";
import {UCUMUnit} from "../../../../helper/UCUMUnit";

/**
 * Component MedicationDSS.vue
 *
 * @module MedicationDSS
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/views/Encounter/MedicationDSS.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'MedicationDSS',
  mixins: [encounterMixin],
  components: {
    FormDatePicker,
  },
  data() {
    return {
      errorMessages: questions.reduce((accumulator, currentItem) => ({
        ...accumulator, [currentItem.key]: '',
      }), {
        LDL: '',
        eGFR: ''
      }),
    };
  },
  computed: {
    today() {
      return moment().toISOString();
    },
    ...questions.reduce((accumulator, currentValue) => ({
      ...accumulator, [currentValue.key]: {
        get() {
          return this.$store.getters[`encounter/${currentValue.key}`];
        },
        set(value) {
          this.$store.dispatch(`encounter/SET_${currentValue.key}`, value);
        },
      },
    }), {}),
    LDLUnitLabel: {
      get() {
        return this.$store.getters['patientSummary/cholesterolLDLPreferredUnitLabel'];
      }
    },
    LDL: {
      get() {
        return this.$store.getters['encounter/LDLInPreferredUnit'];
      },
      set(LDL) {
        this.$store.dispatch('encounter/setLDLInPreferredUnit', LDL);
      },
    },
    eGFR: {
      get() {
        return this.$store.getters['encounter/eGFR'];
      },
      set(eGFR) {
        this.$store.dispatch('encounter/setEGFR', eGFR);
      },
    }
  },
  watch: {
    'HAD_PCI': function (newValue, oldValue) {
      if (oldValue !== null && newValue === false) {
        this.$store.dispatch('encounter/SET_PCI_DATE', '');
      }
    },
  },
};
</script>

<style lang="scss">
.encounter-medication-dss__question {
  white-space: normal !important;
  min-width: 500px;
}
</style>
